import { type FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { BsFileEarmarkPlus, BsFolderPlus, BsTrash, BsCaretDownFill } from 'react-icons/bs';
import { Draggable } from 'react-beautiful-dnd';
import { Button } from 'react-bootstrap';

import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';

import { type RootState } from '@editor2/reducers/root';
import {
	deletePageGroupFromChapter,
	editPageGroupsCollapseState
} from '@editor2/reducers/editor/editorSlice';
import { headerContainer, pageGroupContainer } from '@editor2/containers/ChapterPage/styles';
import { CollapseState } from '@editor2/types/collapseState';
import AddPageModal from '@editor2/components/AddPageModal';
import AddPageGroupModal from '@editor2/components/AddPageGroupModal';
import PageGroupPage from './PageGroupPage';

interface Props {
	pageGroupId: string;
	chapterId: string;
	index: number;
	courseId: number;
}

const PageGroup: FC<Props> = (props) => {
	const { pageGroupId, chapterId, index, courseId } = props;

	const dispatch = useDispatch();
	const { page_groups, course, collapseState } = useSelector(
		(state: RootState) => state.courses[courseId]
	);
	const pageGroup = page_groups.entities[pageGroupId];

	const [isAddPageModalShown, setAddPageModalShown] = useState(false);
	const [isAddPageGroupModalShown, setAddPageGroupModalShown] = useState(false);
	const [collapsePages, setCollapsePages] = useState(false);

	useEffect(() => {
		if (collapseState.page_groups === CollapseState.CollapseAll) {
			setCollapsePages(true);
		}
		if (collapseState.page_groups === CollapseState.ExpandAll) {
			setCollapsePages(false);
		}
	}, [collapseState.page_groups]);

	const onDeletePageGroupClick = () => {
		if (window.confirm(`Are you sure to delete "${pageGroup.name}" page group?`)) {
			dispatch(deletePageGroupFromChapter({ chapterId, pageGroupId, courseId }));
		}
	};

	const onCollapsePageGroupClick = () => {
		dispatch(editPageGroupsCollapseState({ collapseState: CollapseState.Mixed, courseId }));
		setCollapsePages(!collapsePages);
	};

	const pageGroupHeader = (
		<div className={headerContainer}>
			<div className="menu">
				<BsFolderPlus onClick={() => setAddPageGroupModalShown(true)} />
				<BsTrash onClick={() => onDeletePageGroupClick()} />
			</div>

			<div className="headerText">
				<div>
					<span>Page Group: </span>
					<Link to={`/courses/${course.id}/chapters/${chapterId}/page_groups/${pageGroupId}`}>
						{pageGroup.name}
					</Link>
				</div>

				<BsCaretDownFill
					className={collapsePages ? 'collapseIconRotate' : 'collapseIcon'}
					onClick={() => onCollapsePageGroupClick()}
				/>
			</div>
		</div>
	);

	return (
		<Draggable draggableId={pageGroupId} index={index}>
			{(provided) => (
				<div
					className={pageGroupContainer}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}>
					{pageGroupHeader}

					<div className={`pageGroupBody ${collapsePages ? 'listHidden' : 'listShown'}`}>
						<DelayedDroppableWrapper droppableId={pageGroupId} type="Page">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{pageGroup.pages.map((pageId, index) => (
										<PageGroupPage
											pageId={pageId}
											pageGroupId={pageGroupId}
											chapterId={chapterId}
											index={index}
											key={pageId}
											courseId={courseId}
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</DelayedDroppableWrapper>

						<Button
							variant="light"
							size="sm"
							className="addPageButton"
							onClick={() => setAddPageModalShown(true)}>
							<BsFileEarmarkPlus />
							Add page
						</Button>
					</div>

					{isAddPageModalShown && (
						<AddPageModal
							chapterId={chapterId}
							pageGroupId={pageGroupId}
							onHide={() => setAddPageModalShown(false)}
							isShown={isAddPageModalShown}
							courseId={courseId}
						/>
					)}

					{isAddPageGroupModalShown && (
						<AddPageGroupModal
							chapterId={chapterId}
							index={index + 1}
							onHide={() => setAddPageGroupModalShown(false)}
							isShown={isAddPageGroupModalShown}
							courseId={courseId}
						/>
					)}
				</div>
			)}
		</Draggable>
	);
};

export default PageGroup;
